import {
  useQuery as tanStackQuery,
  useMutation as tanStackMutation,
  type UseMutationOptions,
  type UseQueryOptions,
  type QueryKey,
  type QueryFunction,
  queryOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';

export function isAPIAuthError(error: unknown): error is Error & { cause: { status: number } } {
  return Boolean(
    error instanceof Error &&
    error.cause &&
    typeof error.cause === 'object' &&
    error.cause !== null &&
    'status' in error.cause &&
    (error.cause as { status: number }).status === 401,
  );
}

export function isAPI5XXError(error: unknown): error is Error & { cause: { status: number } } {
  return Boolean(
    error instanceof Error &&
    error.cause &&
    typeof error.cause === 'object' &&
    error.cause !== null &&
    'status' in error.cause &&
    (error.cause as { status: number }).status.toString().startsWith('5'),
  );
}

export function handleAPIError(error: unknown, signal?: AbortSignal) {
  if (signal?.aborted) {
    // re-throw and don't show a UI error if the request was aborted (e.g. at initial render)
    throw error;
  }

  if (isAPIAuthError(error)) {
    const toastId = toast.error(
      <div className="flex items-center justify-between w-[300px]">
        <p>API auth failure</p>
        <a href={process.env.PARAMARK_PUBLIC_PYTHON_BASE_URL + '/login/popup'} target="_blank" rel="noreferrer">
          <Button variant="outline" onClick={() => toast.dismiss(toastId)}>
            Refresh your session
          </Button>
        </a>
      </div>,
      {
        duration: Infinity,
        position: 'top-center',
      },
    );
  } else if (isAPI5XXError(error)) {
    // show other errors (e.g. transient network disconnect) as warnings,
    // because Tanstack will retry 3x by default
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    toast.warning(errorMessage);
  }
}

export function useTanStackQuery<TData = unknown, TError = Error, TQueryKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TData, TError, TData, TQueryKey> | unknown,
) {
  const typedOptions = options as UseQueryOptions<TData, TError, TData, TQueryKey>;
  return tanStackQuery(
    queryOptions({
      ...typedOptions,
      queryFn: async (context) => {
        if (!typedOptions.queryFn) {
          throw new Error('queryFn is required');
        }
        try {
          return await (typedOptions.queryFn as QueryFunction<TData, TQueryKey>)(context);
        } catch (error) {
          handleAPIError(error, context.signal);
          throw error; // Re-throw so the query fails properly
        }
      },
    }),
  );
}

// Helper type to extract types from mutation options
type ExtractMutationTypes<T> =
  T extends UseMutationOptions<infer D, infer E, infer V, infer C>
    ? { data: D; error: E; variables: V; context: C }
    : { data: unknown; error: Error; variables: void; context: unknown };

// Function overload that extracts types from UseMutationOptions (handles version mismatch)
export function useTanStackMutation<TOptions extends UseMutationOptions<unknown, unknown, unknown, unknown>>(
  options: TOptions,
): ReturnType<
  typeof tanStackMutation<
    ExtractMutationTypes<TOptions>['data'],
    ExtractMutationTypes<TOptions>['error'],
    ExtractMutationTypes<TOptions>['variables'],
    ExtractMutationTypes<TOptions>['context']
  >
>;

// Function overload for explicit generics
export function useTanStackMutation<TData, TError = Error, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): ReturnType<typeof tanStackMutation<TData, TError, TVariables, TContext>>;

// Implementation
export function useTanStackMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> | unknown,
) {
  const typedOptions = options as UseMutationOptions<TData, TError, TVariables, TContext>;
  return tanStackMutation({
    ...typedOptions,
    mutationFn: async (variables: TVariables) => {
      try {
        if (!typedOptions.mutationFn) {
          throw new Error('mutationFn is required');
        }
        const mutationFn = typedOptions.mutationFn as (variables: TVariables, ...args: unknown[]) => Promise<TData>;
        return await mutationFn(variables);
      } catch (error) {
        handleAPIError(error);
        throw error;
      }
    },
  });
}
